import React, { useMemo, useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
  styled,
  Typography,
  Grid,
} from '@mui/material';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import { delay, startCase } from 'lodash';
import { formatInt } from 'shared/utils/numberFormatters';

import { PropertyUnderwritingPages } from 'stores/RouteStore';
import { useRootStore } from 'stores/RootStore';
import { addressFormatter } from 'utils/formatter';
import { Navigation, RouteItem, SectionName } from 'types/underwriting/navigation';
import { UnderwritingSettings } from 'types/underwriting/underwriting';
import { UnderwritingNavigationConfirmModal } from './UnderwritingNavigationConfirmModal';

export type UnderwritingPageName = (typeof PropertyUnderwritingPages)[number];

const getNavigationMenu = (settings?: UnderwritingSettings) => {
  let submenuItems: Navigation = [{ title: 'Property Summary', routeName: 'propertySummary' }];
  if (settings?.approaches.includes('income')) {
    if (settings?.incomeTypes.includes('residential')) {
      submenuItems.push({
        title: 'Residential',
        section: 'residential',
        children: [
          { title: 'Borrower Rent Roll', routeName: 'residentialRentRoll' },
          { title: 'Rent Comps', routeName: 'residentialRentComps' },
          { title: 'Projected Rent Roll', routeName: 'residentialProjectedRentRoll' },
        ],
      });
    }
    if (settings?.incomeTypes.includes('commercial')) {
      submenuItems.push({
        title: 'Commercial',
        section: 'commercial',
        children: [
          { title: 'Borrower Rent Roll', routeName: 'commercialRentRoll' },
          { title: 'Reimbursements', routeName: 'commercialReimbursements' },
          { title: 'Rent Comps', routeName: 'commercialRentComps' },
          { title: 'Projected Rent Roll', routeName: 'commercialProjectedRentRoll' },
        ],
      });
    }
    submenuItems = [
      ...submenuItems,
      { title: 'Other Income', routeName: 'otherIncome' },
      {
        title: 'Expenses',
        section: 'expenses',
        children: [
          { title: 'Operating Expenses', routeName: 'expenseHistory' },
          { title: 'Expense Comps', routeName: 'expenseComps' },
          { title: 'Expense Forecasts', routeName: 'expenseForecasts' },
        ],
      },
      { title: 'Taxes', routeName: 'taxes' },
      { title: 'Pro Forma', routeName: 'proForma' },
      { title: 'Cap Rate Conclusion', routeName: 'capRateConclusion' },
      { title: 'Financing Terms', routeName: 'financingTerms' },
      { title: 'Export', routeName: 'settingsAndExport' },
    ];
  }

  return submenuItems;
};

type RouteListItemButtonProps = {
  activePage?: UnderwritingPageName;
  item: RouteItem;
  onClick: (routeName: UnderwritingPageName) => void;
  inExpandedSection?: boolean;
};

const RouteListItemButton = ({ activePage, item, onClick, inExpandedSection }: RouteListItemButtonProps) => {
  const isActivePage = item.routeName === activePage;
  return (
    <ListItemButton
      key={item.routeName}
      sx={{ pl: inExpandedSection ? 4 : 2, bgcolor: isActivePage ? 'rgba(25, 118, 210, 0.08)' : 'transparent' }}
      onClick={() => onClick(item.routeName)}
    >
      <ListItemText
        primary={item.title}
        primaryTypographyProps={{
          fontWeight: isActivePage ? 'medium' : 'normal',
          color: item.routeName === activePage ? 'primary' : 'inherit',
        }}
      />
    </ListItemButton>
  );
};

const PageHeader = styled('div')(({ theme }) => ({
  background: 'rgba(250, 250, 250, 0.25)',
  padding: '8px 8px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
}));

interface UnderwritingNavigationProps {
  setNavigation: (routeName: UnderwritingPageName) => void;
  renderContent: () => React.ReactNode;
  activePage?: UnderwritingPageName;
}

const UnderwritingNavigation: React.FC<UnderwritingNavigationProps> = ({
  setNavigation,
  renderContent,
  activePage,
}) => {
  const { underwritingPageStore } = useRootStore();
  const { underwritingData } = underwritingPageStore;
  const navigationMenu = useMemo(() => {
    return getNavigationMenu(underwritingData?.settings);
  }, [underwritingData?.settings]);
  const [confirmModalState, setConfirmModalState] = useState<{
    isOpen: boolean;
    nextRoute?: UnderwritingPageName;
  }>({
    isOpen: false,
    nextRoute: undefined,
  });
  const [expandedSections, setExpandedSections] = useState({
    residential: true,
    commercial: true,
    miscellaneous: true,
    expenses: true,
    sales: true,
  });
  const onRouteClick = (routeName: UnderwritingPageName) => {
    if (!underwritingPageStore.shouldShowConfirmationModalOnPageLeave) {
      setNavigation(routeName);
    } else {
      setConfirmModalState({ isOpen: true, nextRoute: routeName });
    }
  };

  const submitForm = async () => {
    if (underwritingPageStore.form) {
      await underwritingPageStore.form.submit();
    }
  };
  const onConfirmModalSubmit = async () => {
    const routeName: UnderwritingPageName | undefined = confirmModalState.nextRoute || activePage;
    await submitForm();
    if (routeName) {
      delay(setNavigation, 1000, routeName);
    }
    setConfirmModalState({ isOpen: false, nextRoute: undefined });
  };

  const onConfirmModalCancel = () => {
    setConfirmModalState({ isOpen: false, nextRoute: undefined });
  };

  const onConfirmModalReject = () => {
    const routeName: UnderwritingPageName | undefined = confirmModalState.nextRoute || activePage;
    if (routeName) {
      setNavigation(routeName);
    }
    setConfirmModalState({ isOpen: false, nextRoute: undefined });
  };

  const toggleSection = (section: SectionName) => {
    setExpandedSections((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }));
  };
  const property = underwritingPageStore.underwritingData?.property;
  const { propertyType, address, grossBuildingArea, residentialUnits, commercialUnits } = property || {};
  return (
    <>
      <PageHeader>
        <Grid container direction="row" alignItems="center" spacing={1} sx={{ mt: 0 }}>
          <Grid item xs={4}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '18px' }} pl={1}>
              {addressFormatter(address || {})}
            </Typography>
          </Grid>
          <Divider orientation="vertical" flexItem />
          <Grid item xs={1} md={1} sx={{ ml: 2 }}>
            {startCase(propertyType)}
          </Grid>
          <Divider orientation="vertical" flexItem />
          <Grid item container xs={4} md={4} alignItems="center" justifyContent="center" direction="row" spacing={2}>
            <Grid item xs={4} container alignItems="center" direction="column">
              <Grid item>GBA</Grid>
              <Grid item>{formatInt(grossBuildingArea)} SF</Grid>
            </Grid>
            <Grid item xs={4} container alignItems="center" direction="column">
              <Grid item>Residential Units</Grid>
              <Grid item>{formatInt(residentialUnits)}</Grid>
            </Grid>
            <Grid item xs={4} container alignItems="center" direction="column">
              <Grid item>Commercial Units</Grid>
              <Grid item>{formatInt(commercialUnits)}</Grid>
            </Grid>
          </Grid>
        </Grid>
      </PageHeader>

      <Box sx={{ display: 'flex' }}>
        <Drawer
          variant="permanent"
          key="side-navigation"
          sx={{
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              position: 'relative',
            },
          }}
        >
          <List disablePadding>
            {navigationMenu.map((item, index) =>
              'children' in item ? (
                <React.Fragment key={`${item.title}-${item.section}`}>
                  <ListItemButton onClick={() => toggleSection(item.section)}>
                    <ListItemText primary={item.title} />
                    {expandedSections[item.section] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Divider />
                  <Collapse in={expandedSections[item.section]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map((child) => (
                        <RouteListItemButton
                          key={`${child.title}-${child.routeName}`}
                          activePage={activePage}
                          item={child}
                          onClick={() => onRouteClick(child.routeName)}
                          inExpandedSection
                        />
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              ) : (
                <RouteListItemButton
                  key={`${item.title}-${item.routeName}`}
                  activePage={activePage}
                  item={item}
                  onClick={onRouteClick}
                />
              )
            )}
          </List>
        </Drawer>

        {/* Main content */}
        <Box component="main" sx={{ flexGrow: 1, p: 2, bgcolor: '#ffffff', width: '100%', padding: 0, height: '100%' }}>
          {confirmModalState.isOpen && (
            <UnderwritingNavigationConfirmModal
              isOpen={confirmModalState.isOpen}
              onSubmit={onConfirmModalSubmit}
              onCancel={onConfirmModalCancel}
              onReject={onConfirmModalReject}
            />
          )}
          {renderContent()}
        </Box>
      </Box>
    </>
  );
};

export default UnderwritingNavigation;
